/**
 * Widget Demo Page
 *
 * Clean blank page that loads the premium widget v4 in the bottom corner.
 * Simulates how the widget appears on a real website.
 */

import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { API_BASE_URL } from '../utils/constants'

export default function WidgetDemo() {
  const [searchParams] = useSearchParams()
  const chatbotId = searchParams.get('chatbot') || searchParams.get('chatbotId') || ''
  const apiUrl = searchParams.get('apiUrl') || API_BASE_URL
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!chatbotId) {
      setError('No chatbot ID provided. Add ?chatbot=YOUR_ID to the URL.')
      setStatus('error')
      return
    }

    const script = document.createElement('script')
    script.src = `${apiUrl}/static/premium-widget-v4.js?v=${Date.now()}`
    script.async = true

    script.onload = () => {
      try {
        // @ts-ignore - BGAIPremiumWidget is loaded from external script
        if (window.BGAIPremiumWidget) {
          // @ts-ignore
          window.BGAIPremiumWidget.init({
            chatbotId,
            apiUrl: apiUrl || undefined,
          })
          setStatus('ready')
        } else {
          throw new Error('Widget script loaded but BGAIPremiumWidget not found')
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize widget')
        setStatus('error')
      }
    }

    script.onerror = () => {
      setError('Failed to load widget script')
      setStatus('error')
    }

    document.body.appendChild(script)

    return () => {
      // Cleanup on unmount
      document.body.removeChild(script)
      // @ts-ignore
      if (window.BGAIPremiumWidget?.destroy) {
        // @ts-ignore
        window.BGAIPremiumWidget.destroy()
      }
    }
  }, [chatbotId, apiUrl])

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="40" height="40" rx="10" className="fill-primary" />
          <path d="M12 15c0-1.1.9-2 2-2h12a2 2 0 012 2v7a2 2 0 01-2 2h-3l-4 3v-3h-5a2 2 0 01-2-2v-7z" fill="white" />
        </svg>
        <h1 className="text-xl font-semibold text-foreground">CleverAct AI</h1>
        <p className="text-sm text-muted-foreground">Widget Demo</p>
      </div>

      {status === 'loading' && (
        <p className="mt-6 text-sm text-muted-foreground animate-pulse">Loading widget...</p>
      )}
      {status === 'error' && (
        <p className="mt-6 text-sm text-destructive">{error}</p>
      )}
    </div>
  )
}
